import React, { useState, useEffect } from 'react';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import { useToast } from '../components/ToastContext';
import '../css/OpnamestatenVragenbeheer.css';

const OpnamestatenVragenbeheer = () => {
  const toast = useToast();
  const [stepConfigs, setStepConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});

  useEffect(() => {
    fetchStepConfigs();
  }, []);

  const fetchStepConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/inspections/step-config');
      setStepConfigs(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching step configs:', err);
      setError('Kan configuratie niet laden');
    } finally {
      setLoading(false);
    }
  };

  const toggleStepExpansion = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const handleToggleStepVisibility = async (stepId, currentVisible) => {
    try {
      setSaving(true);

      await api.patch(
        `/api/inspections/admin/step-config/${stepId}`,
        { visible: !currentVisible }
      );

      // Update local state
      setStepConfigs(prev =>
        prev.map(step =>
          step.stepId === stepId ? { ...step, visible: !currentVisible } : step
        )
      );
    } catch (err) {
      console.error('Error updating step visibility:', err);
      toast.error('Fout bij opslaan: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFieldVisibility = async (stepId, fieldId, currentVisible) => {
    try {
      setSaving(true);

      await api.patch(
        `/api/inspections/admin/step-config/${stepId}/field/${fieldId}`,
        { visible: !currentVisible }
      );

      // Update local state
      setStepConfigs(prev =>
        prev.map(step => {
          if (step.stepId === stepId) {
            return {
              ...step,
              fields: step.fields.map(field =>
                field.fieldId === fieldId ? { ...field, visible: !currentVisible } : field
              )
            };
          }
          return step;
        })
      );
    } catch (err) {
      console.error('Error updating field visibility:', err);
      toast.error('Fout bij opslaan veld: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    if (!window.confirm('Weet je zeker dat je de configuratie wilt resetten naar standaard? Dit reset ook alle veld-instellingen.')) {
      return;
    }

    try {
      setSaving(true);

      await api.post(
        '/api/inspections/admin/step-config/initialize',
        {}
      );

      await fetchStepConfigs();
      toast.success('Configuratie gereset naar standaard met alle velden');
    } catch (err) {
      console.error('Error initializing config:', err);
      toast.error('Fout bij resetten: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Group fields by category
  const groupFieldsByCategory = (fields) => {
    const grouped = {};
    fields.forEach(field => {
      const category = field.category || 'Overig';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(field);
    });
    return grouped;
  };

  if (loading) {
    return (
      <BeheerdersportaalSidebar>
        <main className="dashboard">
          <div className="loading-message">Configuratie laden...</div>
        </main>
      </BeheerdersportaalSidebar>
    );
  }

  return (
    <BeheerdersportaalSidebar>
      <main className="dashboard">
        <div className="vragenbeheer-container">
          <div className="vragenbeheer-header">
            <h1>Opnamestaat Vragenbeheer</h1>
            <p className="subtitle">
              Beheer welke stappen én vragen zichtbaar zijn in de opnamestaat vragenlijst.
              Verborgen items worden overgeslagen, maar de data blijft beschikbaar.
            </p>
          </div>

          {error && (
            <div className="error-banner">
              {error}
              <button onClick={fetchStepConfigs} className="btn-retry">
                Opnieuw proberen
              </button>
            </div>
          )}

          <div className="actions-bar">
            <button
              onClick={handleInitialize}
              className="btn-reset"
              disabled={saving}
            >
              Reset naar standaard
            </button>
            {saving && <span className="saving-indicator">Opslaan...</span>}
          </div>

          <div className="steps-list">
            {Array.isArray(stepConfigs) && stepConfigs.map((step) => {
              const hasFields = step.fields && step.fields.length > 0;
              const isExpanded = expandedSteps[step.stepId];
              const groupedFields = hasFields ? groupFieldsByCategory(step.fields) : {};

              return (
                <div
                  key={step.stepId}
                  className={`step-card ${!step.visible ? 'hidden-step' : ''} ${isExpanded ? 'expanded' : ''}`}
                >
                  {/* Step Header */}
                  <div className="step-header-row">
                    <div className="step-info">
                      <div className="step-order">#{step.order + 1}</div>
                      <div className="step-details">
                        <h3 className="step-title">{step.title}</h3>
                        <p className="step-id">{step.stepId}</p>
                        {hasFields && (
                          <p className="field-count">{step.fields.length} velden</p>
                        )}
                      </div>
                    </div>

                    <div className="step-controls">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={step.visible}
                          onChange={() => handleToggleStepVisibility(step.stepId, step.visible)}
                          disabled={saving || step.stepId === 'welkom' || step.stepId === 'review'}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className={`status-badge ${step.visible ? 'visible' : 'hidden'}`}>
                        {step.visible ? 'Zichtbaar' : 'Verborgen'}
                      </span>

                      {hasFields && (
                        <button
                          className="expand-button"
                          onClick={() => toggleStepExpansion(step.stepId)}
                          title={isExpanded ? 'Inklappen' : 'Uitklappen'}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      )}
                    </div>
                  </div>

                  {(step.stepId === 'welkom' || step.stepId === 'review') && (
                    <div className="locked-notice">
                      Deze stap kan niet worden verborgen
                    </div>
                  )}

                  {/* Expanded Fields Section */}
                  {hasFields && isExpanded && (
                    <div className="fields-section">
                      <div className="fields-header">
                        <h4>Velden in deze stap</h4>
                      </div>

                      {Object.entries(groupedFields).map(([category, fields]) => (
                        <div key={category} className="field-category">
                          {category !== 'Overig' && (
                            <h5 className="category-title">{category}</h5>
                          )}

                          <div className="fields-list">
                            {fields.map(field => (
                              <div key={field.fieldId} className={`field-item ${!field.visible ? 'field-hidden' : ''}`}>
                                <div className="field-info">
                                  <span className="field-label">{field.label}</span>
                                  <span className="field-type">{field.type}</span>
                                </div>

                                <div className="field-controls">
                                  <label className="toggle-switch-small">
                                    <input
                                      type="checkbox"
                                      checked={field.visible}
                                      onChange={() => handleToggleFieldVisibility(step.stepId, field.fieldId, field.visible)}
                                      disabled={saving}
                                    />
                                    <span className="toggle-slider-small"></span>
                                  </label>
                                  <span className={`field-status ${field.visible ? 'visible' : 'hidden'}`}>
                                    {field.visible ? '✓' : '✗'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {(!Array.isArray(stepConfigs) || stepConfigs.length === 0) && !loading && (
              <div className="no-results">Geen configuratie beschikbaar</div>
            )}
          </div>

          <div className="info-box">
            <h3>ℹ️ Hoe werkt dit?</h3>
            <ul>
              <li>Klik op de ▶ knop om de velden binnen een stap te bekijken</li>
              <li>Verborgen stappen/velden worden overgeslagen in de vragenlijst voor bewoners</li>
              <li>De data-structuur blijft intact, dus bestaande data gaat niet verloren</li>
              <li>De stappen "Welkom" en "Controleren & Indienen" kunnen niet worden verborgen</li>
              <li>Wijzigingen zijn direct actief voor nieuwe opnamestaten</li>
              <li>Je kunt ook individuele velden binnen een stap verbergen</li>
            </ul>
          </div>
        </div>
      </main>
    </BeheerdersportaalSidebar>
  );
};

export default OpnamestatenVragenbeheer;
